import type {
  FilterBarConfig,
  FilterBarState,
  FilterOperatorOption,
} from '../types';

/**
 * Frontend mirror of the filter-operator vocabulary.
 *
 * The owner is `backend/app/queries/query_clause_helpers.py` (the frozensets
 * guarding the `build_*_clause` builders); Python and TypeScript cannot share
 * code, so these lists are the one place the frontend knows which operators
 * each column type accepts. Keep them in step with the Python owner:
 * `articleSearchConfigs.test.ts` pins each list to the backend sets so a
 * vocabulary change on either side fails the suite instead of silently
 * leaving an operator unreachable from the UI (as happened to `within_days`
 * and date `!=` — see issue #479).
 */
// Operators that ignore their value and test for column presence — the
// frontend mirror of the backend's NULL_OPERATORS (its VALUE_OPTIONAL_OPERATORS
// also holds the tag-array checks is_empty/not_empty, but no web filter field
// exposes tags). `operatorNeedsValue` is the single test for "an empty value
// means this filter row is unfinished": the filter bar uses it to decide
// whether to render a value input, and the URL serializer uses it to decide
// whether an empty-value row is incomplete (dropped) or complete (kept). The
// two used to hold private copies of the predicate, so a new value-less
// operator would desync them: the bar would render a stray value input while
// the serializer silently dropped the "incomplete" filter from the URL.
export const VALUELESS_OPERATORS: readonly string[] = ['is_null', 'not_null'];

export function operatorNeedsValue(operator: string): boolean {
  return !VALUELESS_OPERATORS.includes(operator);
}

// The one operator whose value is not a bare scalar: `within_days` carries the
// composite `<date>,<days>` (e.g. "2026-03-15,2"), decoded by the backend's
// build_date_clause. The codec below is the only place the frontend knows that
// format; producers (ArticleFilterBar's two-part input and operator-change
// coercion) and consumers (describeSearch) go through it rather than splitting
// on ',' themselves. An incomplete pair composes to '' — the "unfinished row"
// marker recognised by operatorNeedsValue's callers — so a half-filled input
// is never sent to the backend malformed.
export const WITHIN_DAYS_OPERATOR = 'within_days';

export function composeWithinDaysValue(date: string, days: string): string {
  return date && days ? `${date},${days}` : '';
}

export function parseWithinDaysValue(value: string): { date: string; days: string } {
  const commaIndex = value.indexOf(',');
  if (commaIndex < 0) return { date: value, days: '' };
  return { date: value.slice(0, commaIndex), days: value.slice(commaIndex + 1) };
}

export const TEXT_OPERATORS: FilterOperatorOption[] = [
  { value: 'contains', label: 'contains' },
  { value: 'contains_cs', label: 'contains (case-sensitive)' },
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'not_null', label: 'is set' },
  { value: 'is_null', label: 'is empty' },
];

// The free-text "Any text" field OR-matches across several columns, so only
// substring operators apply.
export const FREE_TEXT_OPERATORS: FilterOperatorOption[] = [
  { value: 'contains', label: 'contains' },
  { value: 'contains_cs', label: 'contains (case-sensitive)' },
];

// `within_days` takes a composite `<date>,<days>` value; ArticleFilterBar
// renders a dedicated two-part input for it.
export const DATE_OPERATORS: FilterOperatorOption[] = [
  { value: '>=', label: 'on or after' },
  { value: '<=', label: 'on or before' },
  { value: '>', label: 'after' },
  { value: '<', label: 'before' },
  { value: '=', label: 'exactly on' },
  { value: '!=', label: 'not on' },
  { value: WITHIN_DAYS_OPERATOR, label: 'within N days of' },
  { value: 'not_null', label: 'is set' },
  { value: 'is_null', label: 'is not set' },
];

export const NUMBER_OPERATORS: FilterOperatorOption[] = [
  { value: '=', label: '= equals' },
  { value: '!=', label: '≠ not equals' },
  { value: '>=', label: '≥ at least' },
  { value: '<=', label: '≤ at most' },
  { value: '>', label: '> greater than' },
  { value: '<', label: '< less than' },
  { value: 'not_null', label: 'is set' },
  { value: 'is_null', label: 'is not set' },
];

export const DEFAULT_ADMIN_FILTER_STATE: FilterBarState = {
  filters: [],
  orderBy: 'overall_score',
  orderDir: 'desc',
};

export const DEFAULT_READER_FILTER_STATE: FilterBarState = {
  filters: [],
  orderBy: 'tier',
  orderDir: 'desc',
};

export const adminArticleFilterConfig: FilterBarConfig = {
  defaultState: DEFAULT_ADMIN_FILTER_STATE,
  fields: [
    { value: 'text', label: 'Any text', type: 'text', operators: FREE_TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'title', label: 'Title', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'domain', label: 'Domain', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'url', label: 'URL', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'summary', label: 'Summary', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'category', label: 'Category', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'published_at', label: 'Published date', type: 'date', operators: DATE_OPERATORS, defaultOperator: '>=' },
    { value: 'created_at', label: 'Ingested date', type: 'date', operators: DATE_OPERATORS, defaultOperator: '>=' },
    { value: 'scoring_completed_at', label: 'Enriched date', type: 'date', operators: DATE_OPERATORS, defaultOperator: '>=' },
    { value: 'enrichment_queued_at', label: 'Queued date', type: 'date', operators: DATE_OPERATORS, defaultOperator: '>=' },
    { value: 'tier', label: 'Score (tier)', type: 'number', operators: NUMBER_OPERATORS, defaultOperator: '>=', min: 1, max: 5, placeholder: '1–5' },
  ],
  sortOptions: [
    { value: 'overall_score', label: 'Overall score', descOnly: true, dirCategory: 'value' },
    { value: 'tier', label: 'Tier', descOnly: true, dirCategory: 'value' },
    { value: 'source_adjustment', label: 'Source/site adjustment', descOnly: true, dirCategory: 'value' },
    { value: 'social_boost', label: 'Social adjustment', descOnly: true, dirCategory: 'value' },
    { value: 'created_at', label: 'Ingested date' },
    { value: 'published_at', label: 'Published date' },
    { value: 'scoring_completed_at', label: 'Enriched date' },
    { value: 'enrichment_queued_at', label: 'Queued date' },
    { value: 'title', label: 'Title', dirCategory: 'value' },
    { value: 'canonical_domain', label: 'Domain', dirCategory: 'value' },
  ],
  presets: [
    {
      id: 'published',
      label: 'Most recently published',
      isActive: (state) => state.filters.length === 0 && state.orderBy === 'published_at' && state.orderDir === 'desc',
      apply: () => ({ filters: [], orderBy: 'published_at', orderDir: 'desc' }),
    },
    {
      id: 'ingested',
      label: 'Most recently ingested',
      isActive: (state) => state.filters.length === 0 && state.orderBy === 'created_at' && state.orderDir === 'desc',
      apply: () => ({ filters: [], orderBy: 'created_at', orderDir: 'desc' }),
    },
    {
      id: 'enriched',
      label: 'Most recently enriched',
      isActive: (state) =>
        state.filters.length === 1 &&
        state.filters[0].field === 'scoring_completed_at' &&
        state.filters[0].operator === 'not_null' &&
        state.orderBy === 'scoring_completed_at' &&
        state.orderDir === 'desc',
      apply: () => ({
        filters: [{ id: crypto.randomUUID(), field: 'scoring_completed_at', operator: 'not_null', value: '' }],
        orderBy: 'scoring_completed_at',
        orderDir: 'desc',
      }),
    },
  ],
};

export const readerArticleFilterConfig: FilterBarConfig = {
  defaultState: DEFAULT_READER_FILTER_STATE,
  fields: [
    { value: 'text', label: 'Any text', type: 'text', operators: FREE_TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'title', label: 'Title', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'domain', label: 'Domain', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'url', label: 'URL', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'summary', label: 'Summary', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'category', label: 'Category', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'author', label: 'Author', type: 'text', operators: TEXT_OPERATORS, defaultOperator: 'contains' },
    { value: 'published_at', label: 'Published date', type: 'date', operators: DATE_OPERATORS, defaultOperator: '>=' },
    { value: 'created_at', label: 'Added date', type: 'date', operators: DATE_OPERATORS, defaultOperator: '>=' },
    { value: 'tier', label: 'Score', type: 'number', operators: NUMBER_OPERATORS, defaultOperator: '>=', min: 1, max: 5, placeholder: '1–5' },
  ],
  sortOptions: [
    { value: 'tier', label: 'Score', descOnly: true, dirCategory: 'value' },
    { value: 'published_at', label: 'Published date' },
    { value: 'created_at', label: 'Added date' },
    { value: 'title', label: 'Title', dirCategory: 'value' },
  ],
  presets: [
    {
      id: 'published',
      label: 'Most recently published',
      isActive: (state) => state.filters.length === 0 && state.orderBy === 'published_at' && state.orderDir === 'desc',
      apply: () => ({ filters: [], orderBy: 'published_at', orderDir: 'desc' }),
    },
    {
      id: 'best',
      label: 'Highest scored',
      isActive: (state) =>
        state.filters.length === 1 &&
        state.filters[0].field === 'tier' &&
        state.filters[0].operator === 'not_null' &&
        state.orderBy === 'tier' &&
        state.orderDir === 'desc',
      apply: () => ({
        filters: [{ id: crypto.randomUUID(), field: 'tier', operator: 'not_null', value: '' }],
        orderBy: 'tier',
        orderDir: 'desc',
      }),
    },
  ],
};
